'use client';

import type { VariantProps } from 'class-variance-authority';
import type { ComponentProps, HTMLAttributes, ReactElement, ReactNode } from 'react';
import { Field as BaseField } from '@base-ui/react/field';
import { Label } from '@savvy-kit/components/label';
import { Separator } from '@savvy-kit/components/separator';
import { cn } from '@savvy-kit/lib/utils';
import { cva } from 'class-variance-authority';
import { isNil, uniqBy } from 'es-toolkit';

export type FieldErrorEntry = { readonly message?: string } | undefined;

type ErrorSurface = {
  readonly error?: boolean;
  readonly errorMessage?: string;
  readonly errors?: readonly FieldErrorEntry[];
};

/**
 * `error`/`errorMessage` and `errors` are two spellings of one state, and they merge rather than one
 * winning: an either-wins rule lets `errors={[]}` swallow an `errorMessage` and render a field that is
 * marked invalid with nothing to read. `error` alone still marks the field invalid without text, which
 * is how `patterns/input-field` and `patterns/date-field` already behave.
 */
function resolveErrors({ error, errorMessage, errors }: ErrorSurface): readonly FieldErrorEntry[] {
  const entries = errors === undefined ? [] : [...errors];
  if (error === true && errorMessage !== undefined && errorMessage !== '') {
    entries.push({ message: errorMessage });
  }
  return entries;
}

function hasError(surface: ErrorSurface): boolean {
  return surface.error === true || resolveErrors(surface).some((entry) => entry?.message);
}

function FieldSet({ className, ...props }: ComponentProps<'fieldset'>) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn(
        'flex flex-col gap-4 has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3',
        className,
      )}
      {...props}
    />
  );
}

function FieldLegend({
  className,
  variant = 'legend',
  ...props
}: ComponentProps<'legend'> & { readonly variant?: 'legend' | 'label' }) {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      className={cn('mb-1.5 font-medium data-[variant=label]:text-sm data-[variant=legend]:text-base', className)}
      {...props}
    />
  );
}

function FieldGroup({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-group"
      className={cn(
        'group/field-group @container/field-group flex w-full flex-col gap-5 has-[>[data-slot=checkbox-group]]:gap-3 *:data-[slot=field-group]:gap-4',
        className,
      )}
      {...props}
    />
  );
}

/**
 * A row holding `FieldContent` aligns to the top rather than the centre, so a wrapped description does
 * not push the control down the block. That leaves a small control optically high against the first
 * line, so it drops by half the difference between the title's 20px line box and its own 16px height.
 * The nudge targets whatever the row's non-content child is, because a control is usually wrapped by
 * its own component rather than sitting in the row as a bare `[role=checkbox]`.
 */
const fieldVariants = cva('group/field flex w-full gap-2 data-[invalid=true]:text-destructive', {
  variants: {
    orientation: {
      vertical: 'flex-col *:w-full [&>.sr-only]:w-auto',
      horizontal:
        'flex-row items-center has-[>[data-slot=field-content]]:items-start *:data-[slot=field-label]:flex-auto has-[>[data-slot=field-content]]:[&>*:not([data-slot=field-content])]:mt-0.5',
      responsive:
        'flex-col *:w-full @md/field-group:flex-row @md/field-group:items-center @md/field-group:*:w-auto @md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:*:data-[slot=field-label]:flex-auto [&>.sr-only]:w-auto @md/field-group:has-[>[data-slot=field-content]]:[&>*:not([data-slot=field-content])]:mt-0.5',
    },
  },
  defaultVariants: {
    orientation: 'vertical',
  },
});

type FieldProps = Omit<ComponentProps<typeof BaseField.Root>, 'render'> &
  VariantProps<typeof fieldVariants> & {
    readonly orientation?: 'vertical' | 'horizontal' | 'responsive';
  };

/**
 * The field container. `Field.Root` is what registers the description and error ids and composes them
 * into `aria-describedby`, so a control only receives that wiring when it is rendered through
 * `FieldControl` inside this element.
 */
function Field({ className, orientation = 'vertical', ...props }: FieldProps) {
  return (
    <BaseField.Root
      data-slot="field"
      data-orientation={orientation}
      className={cn(fieldVariants({ orientation }), className)}
      {...props}
    />
  );
}

function FieldContent({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-content"
      className={cn('group/field-content flex flex-1 flex-col gap-0.5 leading-snug', className)}
      {...props}
    />
  );
}

type FieldLabelProps = Omit<ComponentProps<typeof BaseField.Label>, 'render'> & {
  readonly required?: boolean;
};

function RequiredMark() {
  return (
    <span
      aria-hidden="true"
      data-slot="field-required-mark"
      className="text-destructive leading-none after:content-['*']"
    />
  );
}

function FieldLabel({ className, required, children, ...props }: FieldLabelProps) {
  return (
    <BaseField.Label
      data-slot="field-label"
      render={
        <Label
          className={cn(
            'group/field-label peer/field-label flex w-fit gap-2 leading-snug group-data-[disabled=true]/field:opacity-50 has-data-checked:border-primary/30 has-data-checked:bg-primary/5 has-[>[data-slot=field]]:rounded-lg has-[>[data-slot=field]]:border has-[>[data-slot=field]]:not-has-[:disabled,[data-disabled]]:hover:bg-muted/50 has-[>[data-slot=field]]:has-[:focus-visible]:border-ring has-[>[data-slot=field]]:has-[:focus-visible]:ring-3 has-[>[data-slot=field]]:has-[:focus-visible]:ring-ring/50 *:data-[slot=field]:p-2.5 dark:has-data-checked:border-primary/20 dark:has-data-checked:bg-primary/10',
            'has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col',
            className,
          )}
        />
      }
      {...props}
    >
      {children}
      {required === true ? <RequiredMark /> : null}
    </BaseField.Label>
  );
}

/**
 * The label plus any adornment beside it. The adornment is a SIBLING of the label, never a child: a
 * focusable one inside `<label>` has its clicks forwarded to the control, and its accessible name
 * folded into the label's.
 */
function FieldLabelRow({ className, ...props }: ComponentProps<'div'>) {
  return <div data-slot="field-label-row" className={cn('flex items-center gap-2', className)} {...props} />;
}

// Two roles share this: a heading keeps the weight, a value's name gives it up to `FieldValue`.
function FieldTitle({
  className,
  variant = 'title',
  ...props
}: ComponentProps<'div'> & { readonly variant?: 'title' | 'value-label' }) {
  return (
    <div
      data-slot="field-title"
      data-variant={variant}
      className={cn(
        'flex w-fit items-center gap-2 text-sm group-data-[disabled=true]/field:opacity-50',
        'data-[variant=title]:font-medium data-[variant=value-label]:text-secondary',
        className,
      )}
      {...props}
    />
  );
}

type FieldHeaderProps = {
  readonly label: ReactNode;
  readonly labelIcon?: ReactNode;
  readonly required?: boolean;
  /**
   * False when no control is mounted, whether the field reports a value in its place or has no slot at
   * all. A `<label>` there would carry base-ui's `for` to a control id that is not in the document,
   * which is an invalid association.
   */
  readonly hasControl?: boolean;
};

/** The label (or, with no control, the title) plus its adornment. */
function FieldHeader({ label, labelIcon, required, hasControl = true }: FieldHeaderProps) {
  return (
    <FieldLabelRow>
      {hasControl ? (
        <FieldLabel required={required}>{label}</FieldLabel>
      ) : (
        <FieldTitle variant="value-label">
          {label}
          {required === true ? <RequiredMark /> : null}
        </FieldTitle>
      )}
      {labelIcon}
    </FieldLabelRow>
  );
}

function FieldDescription({ className, ...props }: Omit<ComponentProps<typeof BaseField.Description>, 'render'>) {
  return (
    <BaseField.Description
      data-slot="field-description"
      render={
        <p
          className={cn(
            'text-left text-xs leading-normal font-normal text-muted-foreground group-data-[orientation=horizontal]/field:text-balance [[data-variant=legend]+&]:-mt-1.5',
            // Vertical-rhythm tightening only: in a horizontal row the negative margin lifts this text
            // off the centre line the label and control share.
            'group-data-[orientation=vertical]/field:last:mt-0 group-data-[orientation=vertical]/field:nth-last-2:-mt-1',
            '[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary',
            className,
          )}
        />
      }
      {...props}
    />
  );
}

function FieldSeparator({ children, className, ...props }: ComponentProps<'div'> & { readonly children?: ReactNode }) {
  return (
    <div
      data-slot="field-separator"
      data-content={Boolean(children)}
      className={cn('relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2', className)}
      {...props}
    >
      <Separator className="absolute inset-0 top-1/2" />
      {children ? (
        <span
          className="relative mx-auto block w-fit bg-background px-2 text-muted-foreground"
          data-slot="field-separator-content"
        >
          {children}
        </span>
      ) : null}
    </div>
  );
}

type FieldErrorProps = Omit<ComponentProps<'div'>, 'children'> &
  ErrorSurface & {
    readonly children?: ReactNode;
  };

function hasErrorContent(content: ReactNode): boolean {
  return !isNil(content) && typeof content !== 'boolean' && content !== '';
}

/**
 * `@base-ui/react@1.6.0`'s `field/` module ships no `aria-live` and no `role="alert"`, so the live
 * region is ours to supply. `polite` with `aria-atomic` matches `patterns/date-field` and
 * `patterns/searchable-combobox`, which is the convention on web; the React Native `Field` in
 * `apps/mobile-advisor` announces assertively instead, and that divergence is accepted.
 *
 * The region wrapper stays mounted while `Field.Error` does not: a live region inserted at the same
 * moment as its text is unreliably announced, and `Field.Error` has to unmount so its id leaves the
 * control's `aria-describedby` when the error clears. `match` is passed `true` only when we already
 * decided to render, because `match={false}` falls through to browser validity rather than to off.
 */
function FieldError({ className, children, error, errorMessage, errors, ...props }: FieldErrorProps) {
  let content = children;
  if (!hasErrorContent(content)) {
    const unique = uniqBy(
      resolveErrors({ error, errorMessage, errors }).filter((entry) => entry?.message),
      (entry) => entry?.message,
    );

    if (unique.length === 1) {
      content = unique[0]?.message;
    } else if (unique.length > 1) {
      content = (
        <ul className="ml-4 flex list-disc flex-col gap-1">
          {unique.map((entry) => (
            <li key={entry?.message}>{entry?.message}</li>
          ))}
        </ul>
      );
    }
  }

  // `empty:absolute` pulls the region out of flex flow while it holds no error, so the parent's
  // `gap-2` reserves no space beneath the control. It never goes `display:none` (that would break
  // the live region), so it stays mounted and announces the moment error text is injected.
  return (
    <div data-slot="field-error-region" aria-live="polite" aria-atomic="true" className="empty:absolute">
      {hasErrorContent(content) ? (
        <BaseField.Error
          data-slot="field-error"
          match
          render={<div className={cn('text-xs font-normal text-destructive', className)} />}
          {...props}
        >
          {content}
        </BaseField.Error>
      ) : null}
    </div>
  );
}

/**
 * The wiring base-ui computes for the control: its id, the label association, and the composed
 * `aria-describedby`. This mirrors what `Field.Control` would have spread onto an element slot.
 */
export type FieldControlSlotProps = HTMLAttributes<HTMLElement> & {
  readonly id?: string;
  readonly name?: string;
  readonly disabled?: boolean;
};

type FieldControlProps = Omit<ComponentProps<typeof BaseField.Control>, 'render' | 'children'> & {
  /**
   * The control. An element receives the wiring as DOM props. A function receives it as an argument
   * instead, for a control whose prop surface is closed and does not accept `aria-*` directly, such as
   * `patterns/date-picker` with its `ariaDescribedby` / `ariaInvalid` props.
   */
  readonly children: ReactElement | ((props: FieldControlSlotProps) => ReactElement);
};

/**
 * The only path by which a tier-2 control receives the field's `aria-labelledby` and composed
 * `aria-describedby`. `Field.Root` wires those through `Field.Control` alone, so a control passed as a
 * plain child of `Field` gets neither.
 *
 * `required` also emits `aria-required`: base-ui forwards it as the native attribute, which carries no
 * meaning on a slot that is a button rather than an input, and `patterns/date-field` slots exactly that.
 */
function FieldControl({ children, required, ...props }: FieldControlProps) {
  return (
    <BaseField.Control
      data-slot="field-control"
      required={required}
      aria-required={required === true ? true : undefined}
      render={children}
      {...props}
    />
  );
}

type FieldValueProps = ComponentProps<'div'> & {
  readonly leader?: boolean;
};

/** The read-only presentation of a field's value, replacing the control rather than disabling it. */
function FieldValue({ className, leader = false, children, ...props }: FieldValueProps) {
  return (
    <div
      data-slot="field-value"
      className={cn('flex items-baseline text-sm', leader && 'flex-1 gap-2', className)}
      {...props}
    >
      {/*
        `self-center` rather than the row's baseline: an empty span has no text to sit on, so a
        baseline-aligned rule lands under the descenders and reads as an undershooting underline
        instead of a leader running between the name and the value.
      */}
      {leader ? (
        <span aria-hidden="true" className="min-w-4 flex-1 self-center border-b border-dotted border-border" />
      ) : null}
      <span className={cn('font-medium', leader && 'text-right')}>{children}</span>
    </div>
  );
}

type FieldLayoutProps = ErrorSurface &
  Omit<FieldProps, 'children'> & {
    readonly label?: ReactNode;
    readonly labelIcon?: ReactNode;
    readonly description?: ReactNode;
    readonly required?: boolean;
    readonly readOnlyValue?: ReactNode;
    readonly valueLeader?: boolean;
    readonly children?: ReactElement;
  };

/**
 * The anatomy assembled: label, description, control, error. Routes `children` through `FieldControl`,
 * so the aria wiring cannot be lost by composing the parts in the wrong order.
 */
function FieldLayout({
  label,
  labelIcon,
  description,
  required,
  error,
  errorMessage,
  errors,
  readOnlyValue,
  valueLeader,
  children,
  invalid,
  orientation,
  ...props
}: FieldLayoutProps) {
  const surface = { error, errorMessage, errors };
  const showError = hasError(surface);
  // `ReactNode` admits `null`, and a caller writing `readOnlyValue={editing ? null : value}` means
  // "no value here" rather than "report an empty one", so nullish reads as absent on both branches.
  const showsValue = !isNil(readOnlyValue);
  const hasControl = !showsValue && children !== undefined;
  // A dotted leader runs between the name and the value, so the two have to share a row; stacked, it
  // renders as a rule under the name pointing at nothing.
  const resolvedOrientation = showsValue && valueLeader === true ? 'horizontal' : orientation;

  let slot: ReactNode = null;
  if (showsValue) {
    slot = <FieldValue leader={valueLeader}>{readOnlyValue}</FieldValue>;
  } else if (hasControl) {
    slot = <FieldControl required={required}>{children}</FieldControl>;
  }

  return (
    <Field invalid={invalid === true || showError} orientation={resolvedOrientation} {...props}>
      {label !== undefined ? (
        <FieldHeader label={label} labelIcon={labelIcon} required={required} hasControl={hasControl} />
      ) : null}

      {slot}

      {description !== undefined ? <FieldDescription>{description}</FieldDescription> : null}
      <FieldError {...surface} />
    </Field>
  );
}

export {
  Field,
  FieldContent,
  FieldControl,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldHeader,
  FieldLabel,
  FieldLabelRow,
  FieldLayout,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle,
  FieldValue,
};
